import React, { useEffect, useState } from 'react'
import PageProvider, { usePageState } from '../../../../context/page-context'
import { QuickSearch } from '../../generic-search-react/src/components/quick-search'
import { QuickSearchProps } from '../../generic-search-react/src/components/quick-search/component'
import {
  ActionsMenuItem,
  AppHeaderMenu,
  AppHeaderTabs,
  DropdownHeader,
  DropdownMenu,
  MenuActionProps,
  MenuItem,
  MenuLinkProps,
  PageHeaderMenu,
  ToolbarMenu,
  useTitleChange,
} from '@msaf/core-react'

interface AppHeaderProps {
  hideMenu: (isHidden: boolean) => void
  openMenu: () => void
  user?: {
    initials: string
    firstName: string
    lastName: string
    email: string
    hasValidToken?: boolean
  }
  tags?: Array<string>
  actionsMenuItems?: Array<ActionsMenuItem>
  userMenuLinks: Array<MenuActionProps | MenuLinkProps>
  isDownloadsMenu?: boolean
  isNotificationsMenu?: boolean
  unauthenticatedUserComponent: JSX.Element
  quickSearchConfig?: QuickSearchProps
}

function AppHeader({
  hideMenu,
  openMenu,
  user,
  userMenuLinks,
  tags,
  actionsMenuItems,
  quickSearchConfig,
  isDownloadsMenu = true,
  isNotificationsMenu = true,
  unauthenticatedUserComponent: UnauthenticatedUserComponent,
}: AppHeaderProps) {
  const { pageState } = usePageState()
  useTitleChange(pageState.heading ?? '')

  // Toggle the QuickSearch menu state on search. This flagged is toggled everytime a quick search is performed
  // to trigger a re-render of the `QuickSearch` component.
  // Once all the toolbar menus have been added we can explore other options of toggling individual menus
  const [toggleQuickSearchMenu, setToggleQuickSearchMenu] = useState(false)

  useEffect(() => {
    // Hide the nav panel/menu for full screen pages
    hideMenu(!!pageState.isFullScreen)
  }, [pageState.isFullScreen, hideMenu])

  // Hide app header for full screen pages
  if (pageState.isFullScreen) {
    return null
  }

  return (
    <div className='l-page__header'>
      <header className='c-app-header'>
        <AppHeaderMenu backLink={pageState.backLink} openMenu={openMenu}>
          {/* This is where we can ensure that only user specific content ends up in the user menus */}
          <>
            {quickSearchConfig && user?.hasValidToken && (
              <ToolbarMenu icon='search' iconAriaLabel='Search' closeMenu={toggleQuickSearchMenu}>
                <QuickSearch
                  {...quickSearchConfig}
                  onRowClick={(id: string) => {
                    quickSearchConfig.onRowClick(id)
                    setToggleQuickSearchMenu(!toggleQuickSearchMenu)
                  }}
                />
              </ToolbarMenu>
            )}

            {/* TODO: wire up toolbar menus and remove placeholder booleans above */}
            {isDownloadsMenu && (
              <ToolbarMenu icon='folder' iconAriaLabel='Downloads' badgeCount={1}>
                <DropdownHeader headingText='Downloads' />
              </ToolbarMenu>
            )}

            {isNotificationsMenu && (
              <ToolbarMenu icon='bell' iconAriaLabel='Notifications' badgeCount={99}>
                <DropdownHeader headingText='Notifications' />
              </ToolbarMenu>
            )}

            {user?.hasValidToken ? (
              <ToolbarMenu userInitials={user?.initials ?? ''}>
                <DropdownHeader headingText={`${user?.firstName} ${user?.lastName}`} subheadingText={user?.email} />
                {Array.isArray(userMenuLinks) && (
                  <DropdownMenu>
                    {userMenuLinks.map((menuLink) => (
                      <MenuItem key={menuLink.label} {...menuLink} />
                    ))}
                  </DropdownMenu>
                )}
              </ToolbarMenu>
            ) : (
              UnauthenticatedUserComponent
            )}
          </>
        </AppHeaderMenu>
        <PageHeaderMenu pageHeading={pageState.heading} tags={tags} actionsMenuItems={actionsMenuItems} />
        <AppHeaderTabs tabs={pageState.headerTabs} />
      </header>
    </div>
  )
}

export default function AppShell(props: React.PropsWithChildren<AppHeaderProps>) {
  const { children, ...appHeaderProps } = props
  return (
    <PageProvider>
      <AppHeader {...appHeaderProps} />
      <div className='l-page__content'>{children}</div>
    </PageProvider>
  )
}
